import {GameStateEnums, NetworkEnums} from '../../../../shared/GameEnums'
import {BeginnerHintFormat, BuildGameConfigurations, GameEndData, SelectDiceFormat, SelectPlayerFormat} from '@colonist/game-base-types'
import {MatchmakingStatusUpdate} from '../../../../shared/matchmaking/MatchmakingInterfaces'
import {SocketDataTypes} from '../../../../shared/SocketDataTypes'
import {BaseError} from '../../../../shared/errors/BaseError'
import {
    SelectCardPopupUsingDevelopmentCardFormat, SelectCardPopupForPendingDistributionFormat, SelectCardPopupStealCardFromAnotherPlayerFormat,
    SelectCardToDiscardFormat, SelectCardsToGiveToAnotherPlayer, SelectPlayerUsingDevelopmentCardFormat, SelectPlayerAndResourceCardFormat,
    CardExchangeFormat,
    CardPopupInformation,
} from '@colonist/game-card-types'
import {UIGameManager} from '../ui_game/UIGameManager'
import {CardEnum, GameLogData, GameLogState, MechanicKnightState, NestedPartial, TradeId, TradeState, isFullTradeState} from '@colonist/game-states'
import {SocketLobbySend} from './SocketLobbySend'
import {CA} from '../analytics/Analytics'
import {FriendRoomInviteNotificationData, RoomInviteResponse, UpdateOnlineStatusData, FriendRequestReceivedData} from '../../../../shared/friends/FriendTypes'
import {MarkedAsReadData, NotificationDataWithUnreadCount} from '../../../../shared/NotificationTypes'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {GameDataFirstGameState, BankTransactionLedger, PauseState, BeginnerModeActionState, SocketGameStateUpdate} from '@colonist/game-state-types'
import {UIGeneralServicesController} from '../UIGeneralServicesController'
import {addNewLog} from '@colonist/ui-game'
import {UIGameEndController} from '../ui_game/ui_game_end_screen/UIGameEndController'
import {StorageWrapper} from '../StorageWrapper'

export abstract class SocketActionController {
    protected readonly generalServicesController: UIGeneralServicesController
    abstract readonly uiGameManager: UIGameManager | undefined

    constructor(generalServicesController: UIGeneralServicesController) {
        this.generalServicesController = generalServicesController
    }

    abstract onStateSessionState()

    // eslint-disable-next-line class-methods-use-this
    onStateAutoRejoinGame(gameId: string) {
        SocketLobbySend.reconnectGame(gameId, CA.Label.AutoRejoinGame)
    }

    abstract onRoomListUpdate(data: NetworkEnums.RoomListUpdateData)

    abstract onConnectionEstablished(data: GameDataFirstGameState)
    abstract onStateInitialPlacement()

    onStateCurrentGameState() {
        this.uiGameManager?.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.GameStateUpdated)
    }
    onStateBankState() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.BankStateUpdated)
    }
    onStateMapState(animate: boolean) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.MapStateUpdated)
    }
    onPlayerControllerState(animate: boolean) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.PlayerControllerStateUpdated)
    }
    onSelectPlayer(data: SelectPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayer)
    }
    onGivePlayerResourcesFromTile(data: BankTransactionLedger[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.GivePlayerCardFromTile)
    }
    onExchangeCards(data: CardExchangeFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.ExchangeCards)
    }
    onHighlightCorners(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetCornerHighlight)
    }
    onHighlightRoadEdges(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetRoadEdgeHighlight)
    }
    onHighlightShipEdges(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetShipEdgeHighlight)
    }
    onHighlightTiles(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetTileHighlight)
    }
    onHighlightDiceNumbersForInventor(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.HighlightDiceNumbersForInventor)
    }
    onAmountOfCardsToDiscard(data: SelectCardToDiscardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsToDiscard)
    }
    onSelectCardsToGiveToPlayer(data: SelectCardsToGiveToAnotherPlayer) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardToGiveToAnotherPlayer)
    }
    onSelectPlayerAndCard(data: SelectPlayerAndResourceCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayerAndCard)
    }
    onCardsToSelectForPendingTransaction(data: SelectCardPopupForPendingDistributionFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsForPendingDistribution)
    }
    onConfirmUseDevelopmentCard(data: CardPopupInformation) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.ConfirmUseDevelopmentCard)
    }
    onSelectPlayerUsingDevelopmentCard(data: SelectPlayerUsingDevelopmentCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayerForDevelopmentCard)
    }
    onStealDevelopmentCardFromPlayer(data: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.StealDevelopmentCardFromPlayer)
    }
    onStealResourceCardFromPlayer(data: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.StealResourceCardFromPlayer)
    }
    onClosePopupUI() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.ClosePopupUI)
    }
    onCardsToSelectForDevelopmentCard(data: SelectCardPopupUsingDevelopmentCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsFromBankUsingDevelopmentCard)
    }
    onSelectDice(data: SelectDiceFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectDice)
    }
    onCityImprovementStateUpdated() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.CityImprovementStateUpdated)
    }
    onSpecialBuildPhaseUpdated() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.SpecialBuildPhaseStateUpdated)
    }
    onForceShowCityImprovementButtons() {
        this.uiGameManager?.gameStore.getState().gameActionButtons.setCityImprovementExtraButtonsOpen(true)
    }
    onKnightStateUpdated(knightState: NestedPartial<MechanicKnightState>) {
        this.uiGameManager?.eventController.sendEvent(knightState, UIGameEventEnums.KnightStateUpdated)
    }
    onEmbargoState() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.EmbargoStatusUpdated)
    }
    onCloseOffer(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.RemoveTradeOffer)
    }
    onResetTradeStateAtEndOfTurn() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.TradeResetStateAtEndOfTurn)
    }
    onTradeResponseState(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.TradeResponseState)
    }
    onTradePlayersCreatingCounterOfferState(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.TradePlayersCreatingCounterOfferState)
    }
    onGetOffer(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.NewTradeOfferReceived)
    }
    onBoldTextInTradeWindow(cardsToBold: CardEnum[]) {
        this.uiGameManager?.eventController.sendEvent(cardsToBold, UIGameEventEnums.BoldTextInTradeWindow)
    }
    onBuildGame(configurations: BuildGameConfigurations) {
        this.onStateInitialPlacement()
        if(this.uiGameManager == undefined) return

        this.uiGameManager.buildGame(configurations)
        CA.sendGoogleTagManagerEvent(CA.GoogleTagManagerEvent.GameStarted)
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.FinishedBuildingGame)
    }

    onDiceState(animate: boolean = true) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.DiceStateUpdated)
    }

    onGameEndState(gameEndData: GameEndData, isShownAfterLiveGame?: boolean): UIGameEndController | undefined {
        if(this.uiGameManager == undefined) return

        const gameEndController = new UIGameEndController(this.uiGameManager, gameEndData.endGameState, gameEndData.isReplayAvailable, gameEndData.rankedUserStates, isShownAfterLiveGame)

        this.uiGameManager.gameState.isGameOver = true
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEnd)
        this.uiGameManager.toolTipController.closeTip()

        return gameEndController
    }

    onEndGameText(data: any) {
        const playersJoinedRematch: number = data
        this.uiGameManager?.eventController.sendEvent(playersJoinedRematch, UIGameEventEnums.GameEndRematchTextUpdated)
    }

    onTogglePauseGame(data: any) {
        const pauseState = Object.assign(new PauseState(), data) as PauseState
        this.uiGameManager?.pauseController.togglePause(pauseState)
        this.uiGameManager?.gameStore.getState().gamePause.setIsPaused(pauseState.paused)
        this.uiGameManager?.gameStore.getState().gamePause.setRemainingPauseTimeMS(pauseState.remainingPauseTime)
    }

    abstract onMatchmakingUpdate(matchmakingStatusUpdate: MatchmakingStatusUpdate)
    abstract onMatchmakingError(error: BaseError)
    abstract onMatchmakingShowPreRollAd()
    abstract onMatchmakingDisableAdblock()

    abstract onChannelRoomUpdate(roomData: SocketDataTypes.ChannelRoomUpdate)

    onBeginnerHintActivated(suggestedMove: BeginnerHintFormat) {
        this.uiGameManager?.eventController.sendEvent(suggestedMove, UIGameEventEnums.BeginnerHintReceived)
    }

    onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.BeginnerModeActionUpdated)
    }

    // eslint-disable-next-line class-methods-use-this
    onBeginnerModeLevelEnd() {
        StorageWrapper.incrementBeginnerModeLevel()
    }

    gameStateUpdated(timedGameStateUpdate: SocketGameStateUpdate, animate: boolean = true) {
        if(this.uiGameManager === undefined) return

        const gameStateUpdate = timedGameStateUpdate.diff
        this.uiGameManager.gameController.applyStateUpdates([gameStateUpdate])
        this.uiGameManager.gameController.timeLeftInCurrentState = timedGameStateUpdate.timeLeftInState

        const randomTimeOffsetToTriggerRerender = Math.random() / 10
        this.uiGameManager.gameStore.getState().setTimeLeftInSeconds(timedGameStateUpdate.timeLeftInState + randomTimeOffsetToTriggerRerender)

        if(gameStateUpdate.currentState != undefined || gameStateUpdate.experimentalMechanicState?.mechanicTurnColonistRushState?.playersToggledEndTurn != undefined) this.onStateCurrentGameState()

        // Event dice was broken off of dice state but for now they are always updated together so combining them is fine
        const shouldUpdateDiceState: boolean = gameStateUpdate.diceState != undefined
            || gameStateUpdate.mechanicEventDiceState != undefined

        if(shouldUpdateDiceState) this.onDiceState(animate)

        // Many states were removed from the player state into their own state. But the listeners were not updated to be independent in the UI.
        // So many independent states still need to call `onPlayerControllerState` to avoid breaking things in the UI

        const isMyPlayerActionStateUpdated = gameStateUpdate.experimentalMechanicState?.playerStates?.[this.uiGameManager.gameController.myColor]?.actionState != undefined

        const shouldUpdatePlayerState: boolean = gameStateUpdate.playerStates != undefined
            || gameStateUpdate.mechanicDevelopmentCardsState != undefined
            || gameStateUpdate.mechanicSettlementState != undefined
            || gameStateUpdate.mechanicCityState != undefined
            || gameStateUpdate.mechanicCityWallState != undefined
            || gameStateUpdate.mechanicRoadState != undefined
            || gameStateUpdate.mechanicShipState != undefined
            || gameStateUpdate.mechanicProgressCardsState != undefined
            || gameStateUpdate.mechanicLongestRoadState != undefined
            || gameStateUpdate.mechanicLargestArmyState != undefined
            || isMyPlayerActionStateUpdated
        if(shouldUpdatePlayerState) this.onPlayerControllerState(animate)
        // end of section that needs to update player controller state

        if(gameStateUpdate.bankState != undefined) this.onStateBankState()
        if(gameStateUpdate.tradeState != undefined) this.onTradeStateUpdate(gameStateUpdate.tradeState)

        // A few states were pulled out of map. To make the refactor easier they just all call the old map state logic
        const shouldUpdateMapState: boolean = gameStateUpdate.mapState != undefined
            || gameStateUpdate.mechanicRobberState != undefined
            || gameStateUpdate.mechanicPirateState != undefined
            || gameStateUpdate.mechanicMerchantState != undefined
        if(shouldUpdateMapState) this.onStateMapState(animate)
        if(gameStateUpdate.gameLogState != undefined) this.onGameLogStateUpdate(gameStateUpdate.gameLogState)
        if(gameStateUpdate.mechanicSpecialBuildPhaseState) this.onSpecialBuildPhaseUpdated()
        if(gameStateUpdate.mechanicKnightState) this.onKnightStateUpdated(gameStateUpdate.mechanicKnightState)
        if(gameStateUpdate.mechanicCityImprovementsState) this.onCityImprovementStateUpdated()

        const state = this.uiGameManager.gameController['stateController']['state']
        const keysInDiff = Object.keys(timedGameStateUpdate.diff)
        this.uiGameManager.gameStore.getState().setGameState(state, keysInDiff)
    }

    onTradeStateUpdate(tradeState: NestedPartial<TradeState>) {
        if(tradeState.embargoState != undefined) this.onEmbargoState()

        if(tradeState.activeOffers != undefined) {
            for(const tradeId in tradeState.activeOffers) {
                const tradeStateUpdate = tradeState.activeOffers[tradeId]

                const isTradeClosed = tradeStateUpdate === null
                if(isTradeClosed) {
                    this.onCloseOffer(tradeId)
                    continue
                }

                if(tradeStateUpdate == undefined) continue

                const isPartialTradeStateUpdate = Object.keys(tradeStateUpdate).length == 1
                if(isPartialTradeStateUpdate) {
                    if(tradeStateUpdate.playerResponses != undefined) this.onTradeResponseState(tradeId)
                    if(tradeStateUpdate.playersCreatingCounterOffer != undefined) this.onTradePlayersCreatingCounterOfferState(tradeId)

                    continue
                }

                if(isFullTradeState(tradeStateUpdate)) this.onGetOffer(tradeId)
            }
        }

        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.TradeStateUpdated)
    }

    onGameLogStateUpdate(gameLogsUpdate: NestedPartial<GameLogState>) {
        if(this.uiGameManager === undefined) return
        if(this.uiGameManager.gameController.isReplay) return

        for(const gameLogIndex in gameLogsUpdate) {
            const gameLogData = gameLogsUpdate[gameLogIndex] as GameLogData | undefined
            if(gameLogData == undefined) continue

            addNewLog(Number(gameLogIndex), gameLogData)
        }
    }

    onFriendUpdateOnlineStatus(data: UpdateOnlineStatusData) {
        if(data.sendingAllFriends) return this.generalServicesController.onlineFriendController?.updateAllFriendsOnlineStatus(data.onlineFriendStatuses)
        const singleFriendData = data.onlineFriendStatuses[0]
        this.generalServicesController.onlineFriendController?.updateFriendOnlineStatus(singleFriendData)
    }

    onFriendReceivedRoomInvites(roomInvites: FriendRoomInviteNotificationData[]) {
        this.generalServicesController.onlineFriendController?.receivedRoomInvites(roomInvites)
    }

    onFriendReceivedFriendRequest(friendRequest: FriendRequestReceivedData) {
        this.generalServicesController.onlineFriendController?.receivedFriendRequest(friendRequest)
    }

    onFriendRemoveRoomInvite(roomId: string) {
        this.generalServicesController.onlineFriendController?.removeRoomInvitesWithRoomId(roomId)
    }

    onFriendRoomInviteResponse(data: RoomInviteResponse) {
        this.generalServicesController.onlineFriendController?.friendRespondedToRoomInvite(data)
    }

    onFriendRoomInviteSentSuccessfully(receiverUserId: string) {
        this.generalServicesController.onlineFriendController?.roomInviteSentToFriendSuccessfully(receiverUserId)
    }

    onNotificationsReceived(notificationDataWithUnreadCount: NotificationDataWithUnreadCount) {
        this.generalServicesController.notificationActionController?.addNotificationsToPanel(notificationDataWithUnreadCount)
    }

    onMarkedAsRead(markedAsReadData: MarkedAsReadData) {
        this.generalServicesController.notificationActionController?.markNotificationAsRead(markedAsReadData)
    }
}
